import { useCustomToast } from 'design-system';
import { masterProductsEndpoint } from 'apis/master-products';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import http from '../config/http-service';
import type { MergeResult } from './types';
import { masterProductFamiliesEndpoint } from './use-get-master-product-families-api';

export interface MergeFamilyPayload {
  sourceId: number;
  targetFamilyId: number;
}

type TData = MergeResult;
type TError = AxiosError;
type TVariables = MergeFamilyPayload;

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

// POST api/v1/cms/master-product-families/:sourceId/merge
// Collapses the source family into the target family — variants move,
// colliding variants are relinked/unlinked per the merge-preview result.
export function useMergeFamilyApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ sourceId, targetFamilyId }: TVariables): Promise<TData> => {
    try {
      const response = await http.post(`${masterProductFamiliesEndpoint}/${sourceId}/merge`, { targetFamilyId });
      return response.data.data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${masterProductFamiliesEndpoint}/merge`],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([masterProductFamiliesEndpoint]);
      queryClient.invalidateQueries([masterProductsEndpoint]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
